import React from 'react'
import { BrowserRouter as Router } from 'react-router-dom'
import '../node_modules/devtools-detect/index.js'
import LogoutInactive from './components/LogoutInactive'
import ScrollToTop from './components/ScrollToTop'
import { useWebsocketUser } from './context/webSocketUser'
import User from './models/User'
import { selectLoader, setInitApp } from './redux/actions/common/commonSlice'
import { getUserInfoAction } from './redux/actions/login/login.action'
import { logout, selectUserData, userUpdate } from './redux/actions/login/loginSlice'
import { getSportListAction, getFilteredSportListAction } from './redux/actions/sports/sport.action'
import { useAppDispatch, useAppSelector } from './redux/hooks'
import Routers from './routes'
import WindowFocusHandler from './utils/check-browser-active'
import 'react-toastify/dist/ReactToastify.css'
import authService from './services/auth.service'

const App = () => {
  const ref = React.useRef<any>(null)
  const dispatch = useAppDispatch()

  const { socketUser } = useWebsocketUser()

  const userState = useAppSelector<{ user: User }>(selectUserData)
  const loadingState = useAppSelector(selectLoader)
  
  // ✅ Enhanced: Generate unique tab ID on mount (BEFORE using it)
  const [tabId] = React.useState(() => `tab-${Date.now()}-${Math.random()}`)

  // ✅ CRITICAL: Detect if another tab has SAME ACCOUNT logged in (account-specific)
  React.useEffect(() => {
    // Don't block if user is on login page (allow them to login)
    const currentPath = window.location.pathname
    if (currentPath.includes('/login') || currentPath.includes('/dash')) {
      console.log('ℹ️ On login page - skipping tab detection (allow login)')
      return
    }
    
    // Check if there's an existing active session
    const existingToken = authService.getRefreshToken()
    if (existingToken && userState.user._id) {
      const myUsername = userState.user.username
      console.log('🔍 Tab opened with existing session - checking for SAME ACCOUNT in other tabs')
      console.log('   My username:', myUsername)
      
      // Create BroadcastChannel for tab communication
      const channel = new BroadcastChannel('session-channel')
      
      // Ask if any other tab has SAME username logged in
      channel.postMessage({ 
        type: 'PING', 
        tabId,
        username: myUsername,
        sessionId: userState.user.sessionId
      })
      
      let receivedPong = false
      
      const handlePong = (event: MessageEvent) => {
        if (event.data.type === 'PONG') {
          // Check if it's the SAME account (username match)
          if (event.data.username === myUsername) {
            console.log('🔴 PONG received from another tab with SAME ACCOUNT!')
            console.log('   Same username:', event.data.username)
            console.log('   Other tab ID:', event.data.tabId)
            console.log('   This tab ID:', tabId)
            console.log('   → Same account already logged in another tab!')
            receivedPong = true
            
            // Force logout this tab and redirect to login
            localStorage.removeItem('token')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('login-session')
            alert(`Account "${myUsername}" is already logged in another tab. Only one tab per account is allowed.`)
            window.location.href = '/login'
          } else {
            console.log('✅ PONG from different account - allowing both')
            console.log('   Other account:', event.data.username)
            console.log('   My account:', myUsername)
          }
        }
      }
      
      channel.addEventListener('message', handlePong)
      
      // Wait 500ms to see if we get a PONG from same account
      setTimeout(() => {
        channel.removeEventListener('message', handlePong)
        channel.close()
        if (!receivedPong) {
          console.log('✅ No other tabs with this account - this tab can continue')
        }
      }, 500)
    }
  }, [tabId, userState.user._id, userState.user.username, userState.user.sessionId])

  React.useEffect(() => {
    if (userState.user._id) {
      dispatch(getSportListAction())
      dispatch(getFilteredSportListAction())
      socketUser.emit('userRoomJoin', { userId: userState.user._id })
      socketUser.on('connect', () => {
        socketUser.emit('userRoomJoin', { userId: userState.user._id })
      })
    }
  }, [userState])

  // ✅ Keep BroadcastChannel listener active for NEW_LOGIN events (account-specific)
  React.useEffect(() => {
    if (!userState.user._id) return
    
    const channel = new BroadcastChannel('session-channel')
    const myUsername = userState.user.username
    
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'PING' && event.data.tabId !== tabId) {
        // Another tab is asking if we're active - respond with username
        console.log('📡 PING received from another tab')
        console.log('   Their username:', event.data.username)
        console.log('   My username:', myUsername)
        console.log('   Same account?', event.data.username === myUsername)
        
        // Only respond with PONG if it's the SAME username
        if (event.data.username === myUsername) {
          console.log('   📡 Same account - sending PONG (block them)')
          channel.postMessage({ 
            type: 'PONG', 
            tabId,
            username: myUsername,
            sessionId: userState.user.sessionId
          })
        } else {
          console.log('   ✅ Different account - not sending PONG (allow them)')
        }
      } else if (event.data.type === 'NEW_LOGIN') {
        // Another tab just logged in - check if it's SAME username
        const newUsername = event.data.username
        const newSessionId = event.data.sessionId?.toString()
        const mySessionId = userState.user.sessionId?.toString()
        
        console.log('📢 NEW_LOGIN event received!')
        console.log('   New username:', newUsername)
        console.log('   My username:', myUsername)
        console.log('   New sessionId:', newSessionId)
        console.log('   My sessionId:', mySessionId)
        
        // ✅ CRITICAL: Only logout if SAME username AND different sessionId
        if (newUsername === myUsername && mySessionId && newSessionId && mySessionId !== newSessionId) {
          console.log('🔴 SAME account logged in with different sessionId!')
          console.log('   → Logging out this tab')
          
          dispatch(logout())
          localStorage.removeItem('token')
          localStorage.removeItem('refreshToken')
          localStorage.removeItem('login-session')
          alert(`Account "${myUsername}" was logged in from another tab. Only one tab per account is allowed.`)
          window.location.href = '/login'
        } else if (newUsername !== myUsername) {
          console.log('✅ Different account - both can stay logged in')
          console.log('   They have:', newUsername)
          console.log('   I have:', myUsername)
        } else {
          console.log('✅ Same sessionId - this is MY login, ignoring event')
        }
      }
    }
    
    channel.addEventListener('message', handleMessage)
    
    return () => {
      channel.removeEventListener('message', handleMessage)
      channel.close()
    }
  }, [userState.user._id, userState.user.username, userState.user.sessionId, tabId, dispatch])

  // ✅ Periodic session validation (check every 5 seconds)
  React.useEffect(() => {
    if (!userState.user._id) return

    console.log('🔒 Starting periodic session validation for tab:', tabId)

    const checkSession = async () => {
      try {
        // Don't check if on login page (allow login process to complete)
        const currentPath = window.location.pathname
        if (currentPath.includes('/login') || currentPath.includes('/dash')) {
          // console.log('ℹ️ On login page - skipping session check')
          return
        }
        
        // ✅ ACCOUNT-SPECIFIC: Check if SAME ACCOUNT logged in with new sessionId
        const sessionData = localStorage.getItem('login-session')
        const myUsername = userState.user.username
        const mySessionId = userState.user.sessionId?.toString()
        
        if (sessionData) {
          // Parse username:sessionId format
          const [storedUsername, storedSessionId] = sessionData.split(':')
          
          // Only check if it's the SAME username
          if (storedUsername === myUsername && storedSessionId !== mySessionId) {
            console.log('🔴 SAME account logged in with different sessionId!')
            console.log('   Account:', myUsername)
            console.log('   My sessionId:', mySessionId)
            console.log('   Storage sessionId:', storedSessionId)
            console.log('   → Logging out this tab')
            
            // Logout this tab immediately
            dispatch(logout())
            localStorage.removeItem('token')
            localStorage.removeItem('refreshToken')
            localStorage.removeItem('login-session')
            localStorage.removeItem('current-username')
            alert(`Account "${myUsername}" was logged in from another tab or device. Only one session per account is allowed.`)
            window.location.href = '/login'
            return
          } else if (storedUsername !== myUsername) {
            // console.log('✅ Different account in storage - both allowed')
          }
        }
        
        // Make a lightweight API call to validate session
        // This will trigger session validation on the backend
        await authService.getUser()
        // console.log('✅ Session valid for tab:', tabId)  // Comment out to reduce noise
      } catch (error: any) {
        // Error will be handled by API interceptor
        // Don't log for expected errors during login
        if (!window.location.pathname.includes('/login')) {
          console.log('❌ Session validation failed:', error.message)
        }
      }
    }

    // Check immediately on mount
    checkSession()

    // Then check every 5 seconds (faster detection of session changes)
    const intervalId = setInterval(checkSession, 5000)

    return () => {
      console.log('🔒 Stopping periodic session validation for tab:', tabId)
      clearInterval(intervalId)
    }
  }, [userState.user._id, userState.user.sessionId, tabId, dispatch])

  React.useEffect(() => {
    console.log('🔖 Tab initialized with ID:', tabId)
    
    // Store tab ID in sessionStorage (NOT localStorage - unique per tab)
    sessionStorage.setItem('tabId', tabId)
    
    // Only fetch user info if user has a valid token
    if (authService.getRefreshToken()) {
      console.log('✅ Found existing token - fetching user info')
      dispatch(getUserInfoAction({} as User))
    } else {
      console.log('ℹ️ No token found - user needs to login')
    }

    socketUser.on('logout', ({ sessionId }: { sessionId: string }) => {
      console.log('🔴 Logout event received from server:', { sessionId })
      const localSessionId = localStorage.getItem('login-session')
      console.log('   Local session ID:', localSessionId)
      
      // If the sessionId matches our current session, don't logout (we're the new login)
      if (localSessionId == sessionId) {
        console.log('   ✅ Session IDs match - this is the active session, ignoring logout')
        return
      }
      
      // Different session - we need to logout this tab
      console.log('   ❌ Different session detected - logging out this tab')
      dispatch(userUpdate({} as User))
      setTimeout(() => {
        dispatch(logout())
        localStorage.removeItem('login-session')
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        
        // Show alert to user
        alert('You have been logged out because this account was logged in from another device or browser.')
        
        // Redirect to login page
        window.location.href = '/login'
      }, 100)
    })
  }, [])

  // React.useEffect(() => {
  //   if (loadingState) {
  //     ref.current.continuousStart()
  //   } else {
  //     ref.current.complete()
  //   }
  // }, [loadingState])

  React.useEffect(() => {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // tab is changed
        dispatch(setInitApp(false))
        console.log('tab change')
      } else {
        // tab is active
        dispatch(setInitApp(true))
        console.log('tab active')
      }
    })
  }, [])

  // ✅ NEW: Listen for storage changes (when SAME ACCOUNT logs in elsewhere)
  React.useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Don't process if on login page (allow login to proceed)
      const currentPath = window.location.pathname
      if (currentPath.includes('/login') || currentPath.includes('/dash')) {
        console.log('ℹ️ Storage event ignored - on login page')
        return
      }
      
      // Only process if user is currently logged in
      if (!userState.user._id) {
        console.log('ℹ️ Storage event ignored - user not logged in')
        return
      }
      
      // ✅ ACCOUNT-SPECIFIC: Check if SAME ACCOUNT logged in with new sessionId
      if (e.key === 'login-session' && e.newValue) {
        const myUsername = userState.user.username
        const mySessionId = userState.user.sessionId?.toString()
        
        // Parse username:sessionId from storage
        const [newUsername, newSessionId] = e.newValue.split(':')
        
        console.log('📢 localStorage changed - login-session updated!')
        console.log('   My account:', myUsername, '(sessionId:', mySessionId + ')')
        console.log('   New login:', newUsername, '(sessionId:', newSessionId + ')')
        console.log('   Same account?', newUsername === myUsername)
        
        // ✅ Only logout if SAME username AND different sessionId
        if (newUsername === myUsername && mySessionId && newSessionId && mySessionId !== newSessionId) {
          console.log('🔴 SAME account logged in with different sessionId!')
          console.log('   → Account', newUsername, 'logged in from another device/browser')
          console.log('   → Logging out THIS tab')
          
          dispatch(logout())
          localStorage.removeItem('token')
          localStorage.removeItem('refreshToken')
          localStorage.removeItem('login-session')
          localStorage.removeItem('current-username')
          alert(`Account "${myUsername}" was logged in from another device. Only one session per account is allowed.`)
          window.location.href = '/login'
        } else if (newUsername !== myUsername) {
          console.log('✅ Different account logged in - both can stay active')
          console.log('   They have:', newUsername)
          console.log('   I have:', myUsername)
        } else {
          console.log('✅ SessionId matches - no action needed')
        }
      }
    }
    
    // Add event listener for storage changes
    window.addEventListener('storage', handleStorageChange)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [userState.user.sessionId, userState.user._id, userState.user.username, dispatch])

  return (
    <Router>
      <ScrollToTop />
      <WindowFocusHandler />
      <LogoutInactive />
      {/* <LoadingBar color='#f11946' ref={ref} shadow={true} /> */}
      <Routers />
    </Router>
  )
}

export default App
